import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useFeature } from '../../hooks/useFeature'
import { Search, Plus, Trash2, ArrowRightLeft, Sparkles, Star, ChevronDown, ChevronUp, X, ScanLine, Loader2, BookOpen, Save, Pencil, FileDown, FolderOpen, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { addFoodLog, deleteFoodLog, fetchTodayLogs } from '../../store/nutritionSlice'
import { AppDispatch } from '../../store/store'
import BarcodeScanner from '../../components/BarcodeScanner'
import PhotoFoodLog from '../../components/PhotoFoodLog'
import UsualMealEditorModal from '../../components/UsualMealEditorModal'
import { exportUsualMealPdf, formatMacroTotals } from '../../utils/usualMealPdf'
import { formatFoodQuantity, isPieceUnit, portionFromMaster, portionInputLabel } from '../../utils/foodPortionLabel'
import { foodCivilToday } from '../../utils/foodCivilDate'

const PRESET_MEALS = [
  { key: 'BREAKFAST', label: '🌅 Desayuno' },
  { key: 'LUNCH',     label: '☀️ Almuerzo' },
  { key: 'DINNER',    label: '🌙 Cena' },
  { key: 'SNACK',     label: '🍎 Snack' },
  { key: 'CUSTOM',    label: '⭐ Otra comida' },
]

export default function NutritionPage() {
  const dispatch = useDispatch<AppDispatch>()
  const [activeMeal, setActiveMeal]       = useState('BREAKFAST')
  const [customMealName, setCustomMealName] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [search, setSearch]               = useState('')
  const [foods, setFoods]                 = useState<any[]>([])
  const [selected, setSelected]           = useState<any>(null)
  const [quantity, setQuantity]           = useState('100')
  const [todayLogs, setTodayLogs]         = useState<any>(null)
  const [equivalences, setEquivalences]   = useState<any[]>([])
  const [showEq, setShowEq]               = useState(false)
  const [frequentFoods, setFrequentFoods] = useState<any[]>([])
  const [usualMeals, setUsualMeals]       = useState<any[]>([])
  const [showUsualMeals, setShowUsualMeals] = useState(false)
  const [usualMealModal, setUsualMealModal] = useState<any>(null)
  const [usualPortions, setUsualPortions]  = useState<Record<string, number>>({})
  const [savingMeal, setSavingMeal]        = useState(false)
  const [editorOpen, setEditorOpen]        = useState(false)
  const [editingMeal, setEditingMeal]      = useState<any>(null)
  const [clientFiles, setClientFiles]      = useState<any[]>([])
  const [showClientFiles, setShowClientFiles] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [goals, setGoals]                 = useState<any>(null)
  const [showScanner, setShowScanner]     = useState(false)
  const [scanLoading, setScanLoading]     = useState(false)
  const [scanError, setScanError]         = useState('')
  const hasBarcodeScanner = useFeature('barcode_scanner')
  const hasPhotoFoodLog = useFeature('photo_food_log')
  const scanBusy = useRef(false)

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await api.get('/nutrition/logs/today')
      setTodayLogs(data)
    } catch {
      toast.error('No se pudo cargar el registro de hoy')
    }
  }, [])

  const handleBarcodeScan = async (barcode: string) => {
    if (scanBusy.current) return false
    const code = String(barcode || '').replace(/\D/g, '').trim()
    if (code.length < 8) {
      const msg = 'Código inválido. Deben ser al menos 8 dígitos.'
      setScanError(msg)
      toast.error(msg)
      return false
    }
    scanBusy.current = true
    setScanLoading(true)
    setScanError('')
    try {
      const { data } = await api.get(`/nutrition/foods/barcode/${encodeURIComponent(code)}`)
      const p = data || {}
      const cal = Number(p.caloriesPer100g ?? p.calories_per_100g ?? 0)
      const protein = Number(p.proteinPer100g ?? p.protein_per_100g ?? 0)
      const carbs = Number(p.carbsPer100g ?? p.carbs_per_100g ?? 0)
      const fat = Number(p.fatPer100g ?? p.fat_per_100g ?? 0)

      if (!cal && !protein && !carbs && !fat) {
        const msg = 'Sin datos nutricionales. Búscalo por nombre o agrégalo a mano.'
        toast.error(msg)
        setScanError(msg)
        setSearch(p.name || code)
        setSelected(null)
        return false
      }

      const food = {
        id: p.id || null,
        foodItemId: p.id || null,
        name: p.name || 'Producto escaneado',
        category: p.category || 'Escaneado',
        caloriesPer100g: cal,
        proteinPer100g: protein,
        carbsPer100g: carbs,
        fatPer100g: fat,
        unit: p.unit || 'g',
        gramsPerUnit: p.gramsPerUnit ?? p.grams_per_unit ?? null,
        _isScanned: true,
        _barcode: code,
        _servingSize: null,
      }

      setSelected(food)
      setSearch(food.name)
      setFoods([])
      const isUnit = isPieceUnit(food.unit)
      const serving = Number(p.servingGrams)
      setQuantity(isUnit ? '1' : (serving > 0 ? String(serving) : '100'))
      setShowScanner(false)
      toast.success('¡Producto encontrado: ' + food.name + '!')
      return true
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Producto no encontrado. Prueba escribir el código o búscalo por nombre.'
      const text = Array.isArray(msg) ? msg[0] : String(msg)
      setScanError(text)
      toast.error(text)
      return false
    } finally {
      setScanLoading(false)
      scanBusy.current = false
    }
  }

  const handleAddScanned = async () => {
    if (!selected?._isScanned || !quantity) return
    if (activeMeal === 'CUSTOM' && !customMealName.trim()) {
      toast.error('Escribe el nombre de la comida'); return
    }
    // Catálogo local con id → mismo path que búsqueda manual
    if (selected.foodItemId || selected.id) {
      const payload: any = {
        foodItemId: selected.foodItemId || selected.id,
        quantityGrams: Number(quantity),
        mealType: activeMeal,
      }
      if (activeMeal === 'CUSTOM') payload.customMealName = customMealName.trim()
      const result = await dispatch(addFoodLog(payload))
      if (addFoodLog.fulfilled.match(result)) {
        toast.success('Alimento registrado')
        setSelected(null); setSearch(''); setQuantity('100'); setFoods([])
        loadLogs(); dispatch(fetchTodayLogs())
      } else {
        toast.error('Error al registrar el alimento')
      }
      return
    }
    const factor = Number(quantity) / 100
    // Fallback: macros sin foodItemId (compat)
    try {
      await api.post('/nutrition/logs/recipe', {
        recipeId: null,
        recipeName: selected.name,
        servings: 1,
        mealType: activeMeal,
        calories: Math.round(selected.caloriesPer100g * factor * 100) / 100,
        protein:  Math.round(selected.proteinPer100g  * factor * 100) / 100,
        carbs:    Math.round(selected.carbsPer100g    * factor * 100) / 100,
        fat:      Math.round(selected.fatPer100g      * factor * 100) / 100,
        quantityGrams: Number(quantity),
        logDate: foodCivilToday(),
      })
      toast.success('Alimento registrado')
      setSelected(null); setSearch(''); setQuantity('100'); setFoods([])
      loadLogs(); dispatch(fetchTodayLogs())
    } catch {
      toast.error('Error al registrar el alimento')
    }
  }


  useEffect(() => {
    loadLogs()
    api.get('/nutrition/frequent-foods').then(r => setFrequentFoods(r.data)).catch(() => {})
    api.get('/nutrition/usual-meals').then(r => setUsualMeals(r.data || [])).catch(() => {})
    api.get('/nutrition/goals').then(r => setGoals(r.data)).catch(() => {})
    api.get('/nutrition/client-files').then(r => setClientFiles(r.data || [])).catch(() => {})
    const onFocus = () => { loadLogs(); dispatch(fetchTodayLogs()) }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [loadLogs, dispatch])

  useEffect(() => {
    if (search.length < 2) { setFoods([]); return }
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/nutrition/foods/search', {
          params: { q: search, limit: 40 },
        })
        if (cancelled) return
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        setFoods(list)
      } catch {
        if (!cancelled) {
          setFoods([])
          toast.error('No se pudo buscar alimentos. Intenta de nuevo.')
        }
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [search])

  const deleteUsualMeal = async (mealId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm("¿Eliminar esta comida habitual?")) return
    try {
      await api.delete(`/nutrition/usual-meals/${mealId}`)
      setUsualMeals(prev => prev.filter((m: any) => m.id !== mealId))
    } catch {}
  }

  const registerUsualMeal = async (meal: any) => {
    setUsualMealModal(meal)
    const portions: Record<string, number> = {}
    meal.items?.forEach((_: any, i: number) => { portions[i] = 1 })
    setUsualPortions(portions)
  }

  const confirmUsualMeal = async () => {
    if (!usualMealModal) return
    setSavingMeal(true)
    try {
      const mealType = usualMealModal.mealType || activeMeal
      const items = usualMealModal.items || []
      let saved = 0
      let skipped = 0
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const portions = usualPortions[i] ?? 1
        const hasCatalog = Boolean(item.foodItemId)
        const hasMacros = [item.calories, item.protein, item.carbs, item.fat].some((v: any) => v != null && v !== '')
        if (!hasCatalog && !hasMacros) {
          skipped += 1
          continue
        }
        const result = await dispatch(addFoodLog({
          foodItemId: item.foodItemId || undefined,
          quantityGrams: Math.round(Number(item.quantityGrams || 0) * portions * 100) / 100 || 1,
          mealType,
          customMealName: usualMealModal.name,
          itemName: item.name,
          calories: Math.round(Number(item.calories || 0) * portions * 100) / 100,
          protein: Math.round(Number(item.protein || 0) * portions * 100) / 100,
          carbs: Math.round(Number(item.carbs || 0) * portions * 100) / 100,
          fat: Math.round(Number(item.fat || 0) * portions * 100) / 100,
        }))
        if (addFoodLog.rejected.match(result)) {
          throw new Error((result as any).error?.message || 'Error al registrar')
        }
        saved += 1
      }
      if (saved === 0) {
        toast.error(skipped
          ? 'Esta comida habitual no tiene alimentos del catálogo ni macros'
          : 'No hay ítems para registrar')
        return
      }
      setActiveMeal(mealType)
      await dispatch(fetchTodayLogs())
      await loadLogs()
      toast.success(skipped
        ? `✅ ${usualMealModal.name}: ${saved} registrado(s), ${skipped} omitido(s)`
        : `✅ ${usualMealModal.name} registrada`)
      setUsualMealModal(null)
      setShowUsualMeals(false)
    } catch {
      toast.error('Error al registrar la comida')
    } finally { setSavingMeal(false) }
  }

  const saveCurrentAsUsual = async () => {
    if (!todayLogs) return
    const mealLogs = todayLogs.logs?.filter((l: any) => l.mealType === activeMeal) || []
    if (mealLogs.length === 0) { toast.error('No hay alimentos en esta comida'); return }
    const items = mealLogs.map((l: any) => ({
      foodItemId: l.foodItemId,
      name: l.foodItem?.name || l.customMealName,
      quantityGrams: l.quantityGrams,
      unit: l.foodItem?.unit || l.unit,
      gramsPerUnit: l.foodItem?.gramsPerUnit ?? l.foodItem?.grams_per_unit ?? null,
      calories: l.calories,
      protein: l.protein,
      carbs: l.carbs,
      fat: l.fat,
    }))
    setEditingMeal({
      name: '',
      mealType: activeMeal,
      items,
      totalCalories: mealLogs.reduce((a: number, l: any) => a + Number(l.calories), 0),
      totalProtein: mealLogs.reduce((a: number, l: any) => a + Number(l.protein), 0),
      totalCarbs: mealLogs.reduce((a: number, l: any) => a + Number(l.carbs), 0),
      totalFat: mealLogs.reduce((a: number, l: any) => a + Number(l.fat), 0),
      ingredientsText: '',
      cookTimeMinutes: null,
      notes: '',
      photoUrls: [],
    })
    setEditorOpen(true)
  }

  const openNewUsual = () => {
    setEditingMeal({
      name: '',
      mealType: activeMeal,
      items: [],
      ingredientsText: '',
      cookTimeMinutes: null,
      notes: '',
      photoUrls: [],
    })
    setEditorOpen(true)
  }

  const downloadClientFile = async (file: any) => {
    try {
      const r = await api.get(`/nutrition/client-files/${file.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = file.originalName || 'archivo'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('No se pudo descargar')
    }
  }


  const isUnitFood = isPieceUnit(selected?.unit)
  const preview = selected && quantity ? (() => {
    const portion = portionFromMaster(selected, Number(quantity))
    return {
      calories: portion.calories,
      protein: portion.protein,
      carbs: portion.carbs,
      fat: portion.fat,
    }
  })() : null

  const handleAdd = async () => {
    if (!selected || !quantity) return
    if (selected._isScanned) {
      await handleAddScanned()
      return
    }
    if (activeMeal === 'CUSTOM' && !customMealName.trim()) {
      toast.error('Escribe el nombre de la comida'); return
    }
    const foodItemId = selected.id || selected.foodItemId || selected.fooditemid
    if (!foodItemId) {
      toast.error('Selecciona un alimento de la lista o escanea un código')
      return
    }
    const payload: any = {
      foodItemId,
      quantityGrams: Number(quantity),
      mealType: activeMeal,
    }
    if (activeMeal === 'CUSTOM') payload.customMealName = customMealName.trim()

    const result = await dispatch(addFoodLog(payload))
    if (addFoodLog.fulfilled.match(result)) {
      toast.success('Alimento registrado')
      setSelected(null); setSearch(''); setQuantity('100'); setFoods([])
      loadLogs(); dispatch(fetchTodayLogs())
    } else {
      const msg = (result as any).payload?.message || (result as any).error?.message
      toast.error(msg || 'Error al registrar el alimento')
    }
  }

  const handleDelete = async (id: string) => {
    await dispatch(deleteFoodLog(id))
    loadLogs(); dispatch(fetchTodayLogs())
    toast.success('Registro eliminado')
  }

  const loadEquivalences = async (foodId: string) => {
    const { data } = await api.get(`/nutrition/equivalences/${foodId}`)
    setEquivalences(data); setShowEq(true)
  }

  const selectFood = (f: any) => {
    // normalize field names from raw query or entity
    const normalized = {
      id: f.id || f.fooditemid,
      name: f.name,
      category: f.category,
      unit: f.unit || 'g',
      gramsPerUnit: f.gramsPerUnit || f.grams_per_unit || null,
      caloriesPer100g: f.caloriesPer100g || f.calories_per_100g || 0,
      proteinPer100g:  f.proteinPer100g  || f.protein_per_100g  || 0,
      carbsPer100g:    f.carbsPer100g    || f.carbs_per_100g    || 0,
      fatPer100g:      f.fatPer100g      || f.fat_per_100g      || 0,
    }
    // Set default quantity: 1 unit for unit foods, 100g for gram foods
    const isUnit = isPieceUnit(normalized.unit)
    setQuantity(isUnit ? '1' : '100')
    setSelected(normalized); setFoods([]); setShowSuggestions(false)
  }

  // Suggested portions based on plan
  const getSuggestedGrams = (food: any) => {
    if (!goals || !food.caloriesPer100g) return 100
    const mealCalTarget = goals.dailyCalories / 4
    const g = Math.round((mealCalTarget / food.caloriesPer100g) * 100)
    return Math.min(Math.max(g, 50), 400)
  }

  // All logs across all meals for current meal view
  const mealLogs = activeMeal === 'CUSTOM'
    ? (todayLogs?.logs || []).filter((l: any) => l.mealType === 'CUSTOM')
    : (todayLogs?.byMeal?.[activeMeal] || [])

  const activeMealLabel = activeMeal === 'CUSTOM'
    ? (customMealName ? `⭐ ${customMealName}` : '⭐ Otra comida')
    : PRESET_MEALS.find(m => m.key === activeMeal)?.label || activeMeal

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Registro Nutricional</h1>

      {/* Meal tabs */}
      <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
        {PRESET_MEALS.map(m => (
          <button key={m.key}
            onClick={() => { setActiveMeal(m.key); if (m.key === 'CUSTOM') setShowCustomInput(true) }}
            className={`px-3 py-2 rounded-xl font-medium text-xs whitespace-nowrap transition-colors flex-shrink-0 ${
              activeMeal === m.key ? 'bg-primary-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}>
            {m.label}
            <span className="ml-1 opacity-70">
              ({m.key === 'CUSTOM'
                ? (todayLogs?.logs || []).filter((l: any) => l.mealType === 'CUSTOM').length
                : (todayLogs?.byMeal?.[m.key] || []).length})
            </span>
          </button>
        ))}
      </div>

      {/* Custom meal name input */}
      {activeMeal === 'CUSTOM' && showCustomInput && (
        <div className="card mb-4 flex items-center gap-3">
          <span className="text-gray-500 text-sm flex-shrink-0">Nombre de la comida:</span>
          <input className="input flex-1" placeholder="Ej: Merienda, Media mañana, Antes de dormir..."
            value={customMealName} onChange={e => setCustomMealName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setShowCustomInput(false)} />
          <button onClick={() => setShowCustomInput(false)} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Left: search & add */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-800">Agregar a {activeMealLabel}</h2>
            <button onClick={() => setShowSuggestions(!showSuggestions)}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                showSuggestions ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              <Sparkles size={12} /> Sugerencias
              {showSuggestions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          {/* Suggestions panel */}
          {showSuggestions && (
            <div className="border border-yellow-200 bg-yellow-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-yellow-700 mb-2 flex items-center gap-1">
                <Star size={12} /> Tus alimentos frecuentes
              </p>
              {frequentFoods.length === 0 ? (
                <p className="text-xs text-gray-400">Aún no tienes frecuentes. ¡Registra más comidas!</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {frequentFoods.map((f: any) => {
                    const suggested = getSuggestedGrams(f)
                    return (
                      <button key={f.fooditemid || f.id} onClick={() => {
                        selectFood(f); setQuantity(String(suggested))
                      }}
                        className="w-full flex items-center justify-between text-left px-3 py-2 bg-white rounded-lg hover:bg-primary-50 border border-transparent hover:border-primary-200 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{f.name}</p>
                          <p className="text-xs text-gray-400">{Math.round(f.calories || f.calories_per_100g || 0)} kcal/100g</p>
                        </div>
                        <span className="text-xs text-primary-600 font-medium flex-shrink-0 ml-2">~{suggested}g sugeridos</span>
                      </button>
                    )
                  })}
                </div>
              )}

              {goals && (
                <div className="mt-3 pt-3 border-t border-yellow-200">
                  <p className="text-xs font-semibold text-yellow-700 mb-1 flex items-center gap-1">
                    <Sparkles size={12} /> Basado en tu plan ({Math.round(goals.dailyCalories / 4)} kcal por comida)
                  </p>
                  <p className="text-xs text-yellow-600">
                    Proteína objetivo: ~{Math.round(goals.dailyProteinG / 4)}g · 
                    Carbos: ~{Math.round(goals.dailyCarbsG / 4)}g · 
                    Grasa: ~{Math.round(goals.dailyFatG / 4)}g
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Search — padding-left vía .input-with-leading-icon (food-tailwind !important) */}
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              className="input input-with-leading-icon"
              placeholder="Buscar alimento..."
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null) }}
              aria-label="Buscar alimento"
            />
          </div>
          {hasBarcodeScanner && <button onClick={() => { scanBusy.current = false; setScanError(''); setShowScanner(true) }} className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-green-400 text-green-600 rounded-xl text-sm font-medium hover:bg-green-50 transition-colors">
            📷 Escanear código de barras
          </button>}

          {/* Search results */}
          {foods.length > 0 && !selected && (
            <div className="border border-gray-200 rounded-xl divide-y max-h-52 overflow-y-auto">
              {foods.map(f => (
                <button key={f.id} onClick={() => selectFood(f)}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl">
                  <p className="font-medium text-sm text-gray-800">{f.name}</p>
                  <p className="text-xs text-gray-400">{f.category} · {f.caloriesPer100g ?? f.calories_per_100g} kcal/100g</p>
                </button>
              ))}
            </div>
          )}

          {/* Selected food */}
          {selected && (
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-primary-800">{selected.name}</p>
                  <p className="text-xs text-primary-600">{selected.category}</p>
                </div>
                <div className="flex gap-2">
                  {selected.id && (
                  <button type="button" onClick={() => loadEquivalences(selected.id)}
                    className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                    <ArrowRightLeft size={11} /> Reemplazos
                  </button>
                  )}
                  <button onClick={() => { setSelected(null); setSearch('') }} className="text-gray-400 hover:text-gray-600">
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <label className="text-sm font-medium text-gray-700 flex-shrink-0">
                  {`Cantidad (${portionInputLabel(selected?.unit)}):`}
                </label>
                <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)}
                  className="input w-24 text-center" min="1" max="5000"
                  step={isUnitFood ? '1' : '5'} />
                <span className="text-sm text-gray-500">
                  {portionInputLabel(selected?.unit) === 'unidades' ? 'unid.' : portionInputLabel(selected?.unit)}
                </span>
                {!isUnitFood && goals && (
                  <button onClick={() => setQuantity(String(getSuggestedGrams(selected)))}
                    className="text-xs text-primary-600 hover:underline flex-shrink-0">
                    Sugerido: {getSuggestedGrams(selected)}g
                  </button>
                )}
              </div>

              {preview && (
                <div className="grid grid-cols-4 gap-2 text-center mb-3">
                  {[['Kcal', preview.calories, 'bg-orange-100 text-orange-700'],
                    ['Prot', preview.protein,  'bg-red-100 text-red-700'],
                    ['Carb', preview.carbs,    'bg-yellow-100 text-yellow-700'],
                    ['Grasa', preview.fat,     'bg-blue-100 text-blue-700']
                  ].map(([l, v, cls]) => (
                    <div key={l as string} className={`rounded-lg p-2 ${cls}`}>
                      <p className="text-base font-bold">{v}</p>
                      <p className="text-xs">{l}</p>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={handleAdd} className="btn-primary w-full flex items-center justify-center gap-2 py-2.5">
                <Plus size={16} /> Agregar a {activeMealLabel}
              </button>
            </div>
          )}
        </div>

        {/* Right: today's logs */}
        <div className="card">
          <h2 className="font-bold text-gray-800 mb-4">{activeMealLabel}</h2>
          {mealLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Sin registros en esta comida</p>
              <p className="text-xs mt-1">Busca o selecciona un alimento frecuente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {mealLogs.map((log: any) => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-sm text-gray-800 truncate">
                      {log.foodItem?.name}
                      {log.customMealName && <span className="text-xs text-gray-400 ml-1">({log.customMealName})</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFoodQuantity(log.quantityGrams, log.foodItem?.unit)}
                      {' · '}{Math.round(Number(log.calories) || 0)} kcal
                      {' · '}P {Math.round(Number(log.protein) || 0)}g
                      {' · '}C {Math.round(Number(log.carbs) || 0)}g
                      {' · '}G {Math.round(Number(log.fat) || 0)}g
                    </p>
                  </div>
                  <button onClick={() => handleDelete(log.id)}
                    className="text-gray-300 group-hover:text-red-400 transition-colors flex-shrink-0">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <div className="pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500 font-medium gap-2 flex-wrap">
                <span>{mealLogs.length} alimento(s)</span>
                <span>
                  {Math.round(mealLogs.reduce((a: number, l: any) => a + Number(l.calories || 0), 0))} kcal
                  {' · '}P {Math.round(mealLogs.reduce((a: number, l: any) => a + Number(l.protein || 0), 0))}g
                  {' · '}C {Math.round(mealLogs.reduce((a: number, l: any) => a + Number(l.carbs || 0), 0))}g
                  {' · '}G {Math.round(mealLogs.reduce((a: number, l: any) => a + Number(l.fat || 0), 0))}g
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Equivalences modal */}
      {showEq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-800">Reemplazos para {selected?.name}</h3>
              <button onClick={() => setShowEq(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            {equivalences.length === 0 ? (
              <p className="text-gray-400 text-sm">No hay equivalencias registradas para este alimento.</p>
            ) : (
              <div className="space-y-3">
                {equivalences.map(eq => (
                  <div key={eq.id} className="bg-gray-50 rounded-xl p-3">
                    <p className="font-medium text-gray-800">{eq.replacementFood?.name}</p>
                    <p className="text-sm text-gray-500">
                      {eq.originalQuantityG}g de {selected?.name} = <strong>{eq.replacementQuantityG}g</strong>
                    </p>
                    {eq.notes && <p className="text-xs text-gray-400 mt-1">{eq.notes}</p>}
                    <button onClick={() => {
                      selectFood(eq.replacementFood)
                      setQuantity(String(eq.replacementQuantityG))
                      setShowEq(false)
                    }} className="text-xs text-primary-600 hover:underline mt-1">
                      Usar este reemplazo →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Usual Meals Panel */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white mb-2">
        <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
          <button type="button" onClick={() => setShowUsualMeals(!showUsualMeals)}
            className="font-medium text-gray-700 flex items-center gap-2 text-sm text-left flex-1 min-w-0">
            <BookOpen size={16} className="text-indigo-500 shrink-0" /> Comidas habituales
            {usualMeals.length > 0 && (
              <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{usualMeals.length}</span>
            )}
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => openNewUsual()}
              className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors" title="Nueva comida habitual">
              <Plus size={12} /> Nueva
            </button>
            <button type="button" onClick={() => saveCurrentAsUsual()}
              className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors" title="Guardar comida actual como habitual">
              <Save size={12} /> Guardar actual
            </button>
            <button type="button" onClick={() => setShowUsualMeals(!showUsualMeals)} className="p-1" aria-label="Expandir comidas habituales">
              {showUsualMeals ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>
          </div>
        </div>

        {showUsualMeals && (
          <div className="border-t border-gray-100">
            {usualMeals.length === 0 ? (
              <div className="p-6 text-center text-gray-400">
                <BookOpen size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tienes comidas habituales guardadas</p>
                <p className="text-xs mt-1">Crea una nueva o guarda la comida del día</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                {usualMeals.map((meal: any) => (
                  <div key={meal.id} className="relative text-left p-3 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-200 border border-gray-100 rounded-xl transition-all">
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button type="button" title="Editar" className="p-1.5 text-indigo-600 bg-white border border-indigo-100 rounded-lg shadow-sm"
                        onClick={(e) => { e.stopPropagation(); setEditingMeal(meal); setEditorOpen(true) }}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" title="PDF" className="p-1.5 text-gray-500 bg-white border border-gray-100 rounded-lg shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          void exportUsualMealPdf(meal).then((ok) => {
                            if (!ok) toast.error('No se pudo abrir el PDF. Revisa el bloqueo de ventanas emergentes.')
                          })
                        }}>
                        <FileDown size={13} />
                      </button>
                      <button type="button" title="Eliminar" className="p-1.5 text-red-500 bg-white border border-red-50 rounded-lg shadow-sm"
                        onClick={(e) => deleteUsualMeal(meal.id, e)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <button type="button" className="w-full text-left pr-24" onClick={() => registerUsualMeal(meal)}>
                      <p className="font-medium text-gray-800 text-sm">{meal.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatMacroTotals(meal)}
                        {meal.cookTimeMinutes != null ? ` · ${meal.cookTimeMinutes} min` : ''}
                        {' · '}{(meal.items || []).length} alimento(s)
                      </p>
                      {meal.ingredientsText ? (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{meal.ingredientsText}</p>
                      ) : null}
                      <p className="text-xs text-indigo-500 mt-1">
                        Toca para registrar →
                      </p>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Archivos del coach */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white mb-2">
        <button type="button" onClick={() => setShowClientFiles(!showClientFiles)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
          <span className="font-medium text-gray-700 flex items-center gap-2 text-sm">
            <FolderOpen size={16} className="text-teal-600" /> Archivos de mi entrenador
            {clientFiles.length > 0 && (
              <span className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full">{clientFiles.length}</span>
            )}
          </span>
          {showClientFiles ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>
        {showClientFiles && (
          <div className="border-t border-gray-100 p-3 space-y-2">
            {clientFiles.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Aún no hay archivos compartidos</p>
            ) : clientFiles.map((f: any) => (
              <div key={f.id} className="flex items-center justify-between gap-2 p-2.5 bg-gray-50 rounded-xl">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{f.originalName}</p>
                  <p className="text-xs text-gray-400">{Math.round((f.sizeBytes || 0) / 1024)} KB</p>
                </div>
                <button type="button" className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => downloadClientFile(f)}>
                  <Download size={13} /> Descargar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {hasPhotoFoodLog && (
        <PhotoFoodLog
          mealType={activeMeal}
          customMealName={activeMeal === 'CUSTOM' ? customMealName : undefined}
          onRegistered={() => { loadLogs(); dispatch(fetchTodayLogs()) }}
        />
      )}

      {/* Usual Meal confirmation modal */}
      {usualMealModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <BookOpen size={18} className="text-indigo-500" /> {usualMealModal.name}
              </h3>
              <button onClick={() => setUsualMealModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            <div className="space-y-2 mb-4">
              {(usualMealModal.items || []).map((item: any, i: number) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">{formatFoodQuantity(Number(item.quantityGrams) * (usualPortions[i] ?? 1), item.unit)} · {Math.round(item.calories * (usualPortions[i] ?? 1))} kcal</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => setUsualPortions(p => ({ ...p, [i]: Math.max(0.5, (p[i] ?? 1) - 0.5) }))}
                      className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-sm font-bold">−</button>
                    <span className="text-xs font-bold w-6 text-center">{usualPortions[i] ?? 1}x</span>
                    <button onClick={() => setUsualPortions(p => ({ ...p, [i]: (p[i] ?? 1) + 0.5 }))}
                      className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-sm font-bold">+</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-xl p-3 text-center mb-4">
              <p className="text-sm font-bold text-green-700">
                Total: {Math.round((usualMealModal.items || []).reduce((a: number, item: any, i: number) => a + Number(item.calories) * (usualPortions[i] ?? 1), 0))} kcal
              </p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setUsualMealModal(null)} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={confirmUsualMeal} disabled={savingMeal}
                className="flex-1 py-2.5 bg-indigo-500 text-white rounded-xl text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50 flex items-center justify-center gap-2">
                <Plus size={16} /> {savingMeal ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onDetected={handleBarcodeScan}
          onClose={() => { setShowScanner(false); setScanError('') }}
          busy={scanLoading}
          lookupError={scanError}
        />
      )}

      <UsualMealEditorModal
        open={editorOpen}
        initial={editingMeal}
        onClose={() => { setEditorOpen(false); setEditingMeal(null) }}
        onSaved={async () => {
          const r = await api.get('/nutrition/usual-meals')
          setUsualMeals(r.data || [])
        }}
      />
    </div>
  )
}
